/**
 * usePDF Hook
 * Manages PDF document state and text extraction.
 */
import { useState, useCallback, useRef } from 'react';
import { uploadPDF, getPageText } from '../services/api';
import type { PDFUploadResponse, PageTextResponse } from '../types';

interface UsePDFReturn {
  submissionId: string | null;
  filename: string | null;
  totalPages: number;
  currentPage: number;
  pageText: string;
  loading: boolean;
  error: string | null;
  uploadFile: (file: File) => Promise<void>;
  setCurrentPage: (page: number) => void;
  fetchPageText: (page: number) => Promise<string>;
  getPageTextCached: (page: number) => Promise<string>;
  getAllPagesText: () => Promise<Array<{ page_number: number; text: string }>>;
}

export function usePDF(): UsePDFReturn {
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageText, setPageText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for page text to avoid refetching
  const pageTextCache = useRef<Map<number, string>>(new Map());

  const uploadFile = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    // Clear cache on new upload
    pageTextCache.current.clear();

    try {
      const response: PDFUploadResponse = await uploadPDF(file);
      setSubmissionId(response.submission_id);
      setFilename(response.filename);
      setTotalPages(response.total_pages);
      setCurrentPage(1);

      // Fetch text for first page and cache it
      const pageResponse = await getPageText(response.submission_id, 1);
      setPageText(pageResponse.text);
      pageTextCache.current.set(1, pageResponse.text);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload PDF';
      setError(errorMessage);
      throw new Error(errorMessage); // Re-throw so caller can handle
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPageText = useCallback(
    async (page: number): Promise<string> => {
      if (!submissionId) return '';

      // Check cache first
      const cached = pageTextCache.current.get(page);
      if (cached !== undefined) {
        setPageText(cached);
        return cached;
      }

      try {
        const response: PageTextResponse = await getPageText(submissionId, page);
        setPageText(response.text);
        // Cache the result
        pageTextCache.current.set(page, response.text);
        return response.text;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch page text');
        return '';
      }
    },
    [submissionId]
  );

  // Get page text without updating current page state (for cross-page context)
  const getPageTextCached = useCallback(
    async (page: number): Promise<string> => {
      if (!submissionId || page < 1 || page > totalPages) return '';

      // Check cache first
      const cached = pageTextCache.current.get(page);
      if (cached !== undefined) {
        return cached;
      }

      try {
        const response: PageTextResponse = await getPageText(submissionId, page);
        // Cache the result
        pageTextCache.current.set(page, response.text);
        return response.text;
      } catch (err) {
        // Don't set error state for background fetches
        console.warn(`Failed to fetch text for page ${page}:`, err);
        return '';
      }
    },
    [submissionId, totalPages]
  );

  const handleSetCurrentPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchPageText(page);
    },
    [fetchPageText]
  );

  // Get text for all pages (for document-level feedback)
  const getAllPagesText = useCallback(async (): Promise<
    Array<{ page_number: number; text: string }>
  > => {
    if (!submissionId || totalPages === 0) return [];

    const pages: Array<{ page_number: number; text: string }> = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const text = await getPageTextCached(pageNum);
      pages.push({ page_number: pageNum, text });
    }

    return pages;
  }, [submissionId, totalPages, getPageTextCached]);

  return {
    submissionId,
    filename,
    totalPages,
    currentPage,
    pageText,
    loading,
    error,
    uploadFile,
    setCurrentPage: handleSetCurrentPage,
    fetchPageText,
    getPageTextCached,
    getAllPagesText,
  };
}

export default usePDF;
